// RestrictedHome — premium landing for tenants whose primary modules are
// disabled. Rendered when no priority module is reachable (or shown as the
// "next steps" card grid inside FeatureDisabledScreen).
//
// Goal: even heavily restricted tenants feel they are using a customized
// premium platform, not a broken/empty one.
//
// iter177.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { listEnabledLandings } from '../config/modules';
import { Card, CardContent } from './ui/card';
import {
  LayoutDashboard, BarChart3, Users, Sparkles, ClipboardList,
  Package, ChefHat, Wallet, MessageSquare, Megaphone, UserCog,
  Building2, Settings, ArrowUpRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICONS = {
  '/admin/dashboard': LayoutDashboard,
  '/admin/pnl': BarChart3,
  '/admin/cost-insights': BarChart3,
  '/admin/insights': BarChart3,
  '/admin/customers': Users,
  '/admin/customer-experience': Sparkles,
  '/admin/cx': Sparkles,
  '/admin/checklist': ClipboardList,
  '/admin/inventory': Package,
  '/admin/recipes': ChefHat,
  '/admin/petty-cash': Wallet,
  '/admin/action-comm': MessageSquare,
  '/admin/marketing': Megaphone,
  '/admin/managers': UserCog,
  '/admin/outlets': Building2,
  '/admin/settings': Settings,
  '/manager/entry': ClipboardList,
  '/manager/pnl': BarChart3,
  '/manager/kitchen': ChefHat,
  '/manager/bar': Wallet,
  '/manager/cost-insights': BarChart3,
  '/manager/checklist': ClipboardList,
};

const DESCRIPTIONS = {
  '/admin/dashboard':           'Live KPIs, sales, occupancy, and operational pulse at a glance.',
  '/admin/pnl':                 'Real-time P&L, financial review, statements, and management reports.',
  '/admin/cost-insights':       'Cost analysis, food + beverage cost trends, margin intelligence.',
  '/admin/insights':            'Insights & reports across all operations.',
  '/admin/customers':           'CRM, guest profiles, repeat-guest mapping, loyalty.',
  '/admin/customer-experience': 'NPS, sentiment, complaint themes, service-recovery workflows.',
  '/admin/cx':                  'Custom QR feedback forms, dynamic analytics, premium PDF/Excel reports.',
  '/admin/checklist':           'Operational checklists, audits, recurring tasks.',
  '/admin/inventory':           'Stock levels, opening stock, recipe-driven consumption.',
  '/admin/recipes':             'Recipe library, costing, pricing intelligence.',
  '/admin/petty-cash':          'Petty-cash logs, approvals, and reconciliation.',
  '/admin/action-comm':         'Internal communication, broadcasts, action items.',
  '/admin/marketing':           'Campaigns, promotions, marketing intelligence.',
  '/admin/managers':            'User management, roles, module assignment.',
  '/admin/outlets':             'Outlet configuration, branding, module gating.',
  '/admin/settings':            'Profile, preferences, and account settings.',
  '/manager/entry':             'Daily business report — sales, expenses, deposits.',
  '/manager/pnl':               'P&L view for your outlet.',
  '/manager/kitchen':           'Kitchen closing — wastage, leftovers, end-of-day.',
  '/manager/bar':                'Bar closing — opening/closing stock, sales reconciliation.',
  '/manager/cost-insights':     'Outlet cost intelligence.',
  '/manager/checklist':         'Operational checklists for your outlet.',
};

export default function RestrictedHome() {
  const { user, tenantFeatures } = useAuth();
  const [branding, setBranding] = useState(null);
  const enabledModules = listEnabledLandings(user?.role, tenantFeatures, user);

  // Load tenant branding so the welcome line uses the property name + logo.
  useEffect(() => {
    axios.get(`${API}/branding`).then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const brandName = branding?.brand_name || branding?.platform_name || '';
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = (user?.name || user?.email || '').split(/[\s@]/)[0];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-[#F7F9F6] via-[#FAFBF8] to-[#F0F4F0]"
         data-testid="restricted-home">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        {/* ─── Hero ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 mb-6">
          {branding?.logo_url || branding?.has_logo ? (
            <div className="bg-white border border-[#E2E8DF] rounded-xl p-2.5 shadow-sm">
              <img
                src={branding.logo_url || `${API}/branding/platform/logo`}
                alt={brandName}
                className="h-10 w-auto object-contain"
              />
            </div>
          ) : null}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-medium">
              {brandName ? `${brandName} — Operations Portal` : 'Operations Portal'}
            </div>
            <h1 className="text-[26px] sm:text-[32px] font-semibold text-[#1A2421] leading-tight tracking-tight mt-1">
              {greeting}{firstName ? `, ${firstName}` : ''}.
            </h1>
          </div>
        </div>
        <p className="text-[14.5px] text-[#5C6B64] leading-relaxed max-w-2xl mb-9"
           data-testid="restricted-home-subtitle">
          {enabledModules.length > 0
            ? 'Your portal is configured for your operations. Pick a module below to continue.'
            : "Your account is active but no modules are currently enabled. Please reach out to your administrator — they can grant access from the platform settings."}
        </p>

        {/* ─── Module grid ─────────────────────────────────────── */}
        {enabledModules.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="restricted-home-modules">
            {enabledModules.map((m) => {
              const Icon = ICONS[m.path] || LayoutDashboard;
              return (
                <Link key={m.path} to={m.path}
                      className="group block"
                      data-testid={`restricted-home-card-${m.feature || 'open'}`}>
                  <Card className="border-[#E2E8DF] hover:border-[color:var(--color-primary)] hover:shadow-md transition-all duration-200 h-full group-hover:-translate-y-0.5">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#EEF4EF] text-[color:var(--color-primary)] flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#9AAA9F] group-hover:text-[color:var(--color-primary)] group-hover:rotate-12 transition-all" />
                      </div>
                      <div className="text-[15px] font-semibold text-[#1A2421] mb-1">{m.label}</div>
                      <div className="text-[12px] text-[#5C6B64] leading-relaxed line-clamp-2">
                        {DESCRIPTIONS[m.path] || 'Open module'}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <Card className="border-[#D89B5B]/30 bg-[#FFFBF4]" data-testid="restricted-home-empty">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 text-[#D89B5B] mx-auto mb-2" />
              <div className="text-[14px] font-medium text-[#1A2421] mb-1">
                No modules are enabled yet.
              </div>
              <div className="text-[12.5px] text-[#5C6B64] max-w-md mx-auto">
                Your administrator can enable modules from the platform's Super Admin
                control panel. Once enabled, they'll appear here automatically.
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
